import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import MedicationSourceReview from '../../features/careSourceProcessing/MedicationSourceReview';
import { fetchCareSourceById } from '../../features/careSourceProcessing/careSourceActivityService';
import { fetchMedications } from '../../services/medicationService';
import {
  subscribeToMedicationReminderRounds,
  subscribeToMedicationReminderSchedules,
} from '../../services/medicationReminderService';
import { formatReminderSchedule } from '../../features/medicationReminders/medicationReminderScheduleAdapter';
import { useRole } from '../../contexts/RoleContext';
import { CARE_PROFILE_ROUTE } from '../../constants/profileRoutes';
import {
  buildMedicationReviewReturnState,
  getMedicationReviewNavigationContext,
} from '../../utils/medicationReviewNavigation';

const MedicationReviewPage = () => {
  const { childId, sourceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { childrenWithAccess = [], loading: roleLoading } = useRole();
  const child = childrenWithAccess.find((item) => item.id === childId) || null;
  const hasChildAccess = Boolean(child);
  const [source, setSource] = React.useState(null);
  const [medications, setMedications] = React.useState([]);
  const [reminderSchedules, setReminderSchedules] = React.useState([]);
  const [reminderRounds, setReminderRounds] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (roleLoading || !hasChildAccess) return undefined;

    let active = true;
    Promise.all([
      fetchCareSourceById(sourceId),
      fetchMedications(childId, true),
    ]).then(([nextSource, nextMedications]) => {
      if (!active) return;
      setSource(nextSource);
      setMedications(nextMedications);
      setLoading(false);
    }).catch((nextError) => {
      if (!active) return;
      setError(nextError?.message || 'Medication review could not be loaded.');
      setLoading(false);
    });
    return () => { active = false; };
  }, [childId, hasChildAccess, roleLoading, sourceId]);

  React.useEffect(() => {
    if (roleLoading || !hasChildAccess) return undefined;
    const stopSchedules = subscribeToMedicationReminderSchedules(childId, setReminderSchedules, () => {});
    const stopRounds = subscribeToMedicationReminderRounds(childId, setReminderRounds, () => {});
    return () => {
      if (typeof stopSchedules === 'function') stopSchedules();
      if (typeof stopRounds === 'function') stopRounds();
    };
  }, [childId, hasChildAccess, roleLoading]);

  const reminderStateByMedicationId = React.useMemo(() => {
    const states = new Map();
    reminderSchedules.forEach((schedule) => {
      if (!schedule.medicationId) return;
      states.set(schedule.medicationId, {
        configured: true,
        active: schedule.active !== false && !schedule.paused,
        label: schedule.active === false || schedule.paused
          ? 'Reminder paused'
          : `Reminder set · ${formatReminderSchedule(schedule)}`,
      });
    });
    reminderRounds.forEach((round) => {
      (round.members || []).forEach((member) => {
        if (!member.medicationId) return;
        const roundName = round.name || round.label || 'Medication round';
        const roundTime = /^\d{2}:\d{2}$/.test(round.reminderTime || '')
          ? new Date(`2000-01-01T${round.reminderTime}:00`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
          : '';
        states.set(member.medicationId, {
          configured: true,
          active: round.active !== false,
          label: round.active === false
            ? `${roundName} paused`
            : `${roundName}${roundTime ? ` · ${roundTime}` : ''}`,
        });
      });
    });
    return states;
  }, [reminderRounds, reminderSchedules]);

  const reviewNavigation = React.useMemo(() => getMedicationReviewNavigationContext({
    childId,
    returnTo: CARE_PROFILE_ROUTE,
    originSourceId: sourceId,
    ...(location.state?.medicationReviewNavigation || {}),
  }), [childId, location.state, sourceId]);
  const returnFromReview = React.useCallback(() => {
    const returningToMedicationProfile = reviewNavigation.returnTo === CARE_PROFILE_ROUTE;
    navigate(reviewNavigation.returnTo, {
      state: {
        ...buildMedicationReviewReturnState(reviewNavigation),
        ...(returningToMedicationProfile ? { openChildMedicationManager: true } : {}),
      },
    });
  }, [navigate, reviewNavigation]);
  const openReminderForMedication = React.useCallback((medication) => {
    if (!medication?.id) return;
    navigate(`/children/${childId}/medication-reminders?medicationId=${encodeURIComponent(medication.id)}`, {
      state: {
        returnTo: location.pathname,
        returnState: { medicationReviewNavigation: reviewNavigation },
      },
    });
  }, [childId, location.pathname, navigate, reviewNavigation]);

  if (roleLoading && !childrenWithAccess.length) {
    return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }
  if (!roleLoading && !childrenWithAccess.some((child) => child.id === childId)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (loading) {
    return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !source || source.childId !== childId) {
    return <Box sx={{ p: 2 }}><Alert severity="warning">{error || 'This medication source is not available.'}</Alert></Box>;
  }

  return (
    <MedicationSourceReview
      source={source}
      childName={child?.name || ''}
      existingMedications={medications}
      open
      pageLayout
      onBack={returnFromReview}
      onSetReminder={openReminderForMedication}
      reminderStateByMedicationId={reminderStateByMedicationId}
      onError={setError}
      medicationReviewNavigation={reviewNavigation}
      onMedicationListChanged={({ type, medication } = {}) => {
        if (!medication?.id) return;
        if (type === 'removed') {
          setMedications((current) => current.filter((entry) => entry.id !== medication.id));
          return;
        }
        setMedications((current) => current.some((entry) => entry.id === medication.id)
          ? current.map((entry) => entry.id === medication.id ? medication : entry)
          : [...current, medication]);
      }}
      onReviewComplete={({ action } = {}) => {
        if (action === 'mark_reviewed') returnFromReview();
      }}
    />
  );
};

export default MedicationReviewPage;
